// Block-level renderers: signatures, bibliography, footnotes.
//
// Footer blocks (Author Bio / Product CTA / Affiliate CTA), callouts,
// pull-quotes, and image-captions were removed — those node types no
// longer exist. Footer-block content lives in `composer_blocks` now.

import type {
  RenderContext,
  RenderReference,
  ResolvedFootnote,
  SignatureImageNode,
} from "../types";
import { escapeHtml, escapeMd, type RenderFormat } from "./inline";
import { footnoteBodyId, footnoteRefId } from "../footnoteAnchors";
import { renderFootnoteBodyHtml, renderFootnoteBodyMd } from "../footnoteBody";

const SIGNATURE_HEIGHT_PX: Record<"small" | "medium" | "large", number> = {
  small: 48,
  medium: 72,
  large: 96,
};

export const renderSignatureImage = (node: SignatureImageNode, fmt: RenderFormat): string => {
  const { src, alt } = node.attrs;
  const size = node.attrs.size ?? "medium";
  const align = node.attrs.align ?? "left";
  if (fmt === "html") {
    const heightPx = SIGNATURE_HEIGHT_PX[size];
    const justify = align === "center" ? "center" : "flex-start";
    return `<div class="signature-image signature-image--${size} signature-image--${align}" data-signature-image="true" style="display:flex;justify-content:${justify};margin:-0.5rem 0 0.25rem;"><img src="${escapeHtml(src)}" alt="${escapeHtml(alt)}" style="height:${heightPx}px;width:auto;object-fit:contain;display:block;margin:0;" /></div>`;
  }
  return `![${escapeMd(alt)}](${src})`;
};

/** Sort references alphabetically by their bibliographyText (which typically
 *  starts with author Last, First in the configured citation style). */
const sortRefs = (items: RenderReference[]): RenderReference[] =>
  [...items].sort((a, b) =>
    a.bibliographyText.localeCompare(b.bibliographyText, undefined, { sensitivity: "base" }),
  );

/**
 * The Bibliography is a single section titled "Bibliography" — never split into
 * "Works Cited" / "Works Consulted". Cited works are evidenced by the
 * footnotes/endnotes; the Bibliography is works-consulted by inclusion. All
 * references (cited + consulted) merge into one alphabetized list, deduped by id.
 */
export const renderBibliographyBlock = (ctx: RenderContext, fmt: RenderFormat): string => {
  const seen = new Set<string>();
  const merged: RenderReference[] = [];
  for (const r of ctx.references) {
    if (seen.has(r.id)) continue;
    seen.add(r.id);
    merged.push(r);
  }
  const items = sortRefs(merged);
  if (items.length === 0) return "";
  if (fmt === "html") {
    const lis = items.map((r) => `<li>${r.bibliographyHtml}</li>`).join("");
    return `<section class="bibliography-section"><h3>Bibliography</h3><ul class="bibliography">${lis}</ul></section>`;
  }
  const lis = items.map((r) => `- ${r.bibliographyText}`).join("\n");
  return `### Bibliography\n\n${lis}`;
};

export const renderFootnotesBlock = (footnotes: ResolvedFootnote[], fmt: RenderFormat): string => {
  if (footnotes.length === 0) return "";
  if (fmt === "html") {
    const items = footnotes
      .map((f) => `<li id="${footnoteBodyId(f.id)}">${renderFootnoteBodyHtml(f.body)} <a href="#${footnoteRefId(f.id)}" class="footnote-back">↩</a></li>`)
      .join("");
    return `<section class="footnotes"><ol>${items}</ol></section>`;
  }
  return footnotes.map((f) => `[^${f.number}]: ${renderFootnoteBodyMd(f.body)}`).join("\n");
};
